import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceAsset, 
  AssetStats, 
  AssetFilters, 
  AssetHealthLog, 
  AssetGroup, 
  AssetType, 
  AssetImage 
} from '../types/assets.types';

export const AssetsService = {
  /**
   * Lấy danh sách tài sản với bộ lọc
   */
  async getAssets(companyId: string, filters: AssetFilters = {}): Promise<{ data: MaintenanceAsset[], total: number }> {
    let query = supabase
      .from('assets')
      .select(`
        *,
        project:projects(id, name, code),
        zone:project_zones(id, zone_name, zone_code)
      `, { count: 'exact' })
      .eq('company_id', companyId)
      .is('deleted_at', null);

    // Apply Filters
    if (filters.search) {
      query = query.or(`asset_name.ilike.%${filters.search}%,asset_code.ilike.%${filters.search}%`);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }
    if (filters.health && filters.health !== 'all') {
      query = query.eq('health_status', filters.health);
    }
    if (filters.type && filters.type !== 'all') {
      query = query.eq('asset_type_code', filters.type);
    }
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.zoneId) {
      query = query.eq('zone_id', filters.zoneId);
    }

    // Pagination
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceAsset[],
      total: count || 0
    };
  },

  /**
   * Lấy chi tiết tài sản
   */
  async getAssetDetail(id: string): Promise<MaintenanceAsset | null> {
    const { data, error } = await supabase
      .from('assets')
      .select(`
        *,
        project:projects(id, name, code),
        zone:project_zones(id, zone_name, zone_code)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as MaintenanceAsset;
  },

  /**
   * Tạo tài sản mới
   */
  async createAsset(data: Partial<MaintenanceAsset>): Promise<MaintenanceAsset> {
    const { data: result, error } = await supabase
      .from('assets')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceAsset;
  },

  /**
   * Cập nhật tài sản
   */
  async updateAsset(id: string, updates: Partial<MaintenanceAsset>): Promise<MaintenanceAsset> {
    const { data, error } = await supabase
      .from('assets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceAsset;
  },

  /**
   * Xóa mềm tài sản
   */
  async deleteAsset(id: string): Promise<void> {
    const { error } = await supabase
      .from('assets')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Lấy thống kê tài sản
   */
  async getAssetStats(companyId: string): Promise<AssetStats> {
    const { data, error } = await supabase
      .from('assets')
      .select('health_status, asset_type_code')
      .eq('company_id', companyId)
      .is('deleted_at', null);

    if (error) throw error;

    const stats: AssetStats = {
      total: data.length,
      goodHealth: data.filter(a => a.health_status === 'good').length,
      warningHealth: data.filter(a => a.health_status === 'warning').length,
      badHealth: data.filter(a => a.health_status === 'bad').length,
      byType: data.reduce((acc, a) => {
        const type = a.asset_type_code as string;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as any),
    };

    return stats;
  },

  /**
   * Lấy lịch sử sức khỏe tài sản
   */
  async getAssetHealthLogs(assetId: string): Promise<AssetHealthLog[]> {
    const { data, error } = await supabase
      .from('asset_health_logs')
      .select(`
        *,
        checker:user_profiles!checked_by(id, full_name)
      `)
      .eq('asset_id', assetId)
      .order('checked_at', { ascending: false });

    if (error) throw error;
    return (data || []) as AssetHealthLog[];
  },

  /**
   * Tạo nhật ký sức khỏe mới
   */
  async createAssetHealthLog(log: Partial<AssetHealthLog>): Promise<AssetHealthLog> {
    const { data, error } = await supabase
      .from('asset_health_logs')
      .insert([log])
      .select()
      .single();

    if (error) throw error;
    return data as AssetHealthLog;
  },

  /**
   * Lấy danh sách nhóm tài sản
   */
  async getAssetGroups(projectId: string): Promise<AssetGroup[]> {
    const { data, error } = await supabase
      .from('asset_groups')
      .select('*')
      .eq('project_id', projectId)
      .order('group_name', { ascending: true });

    if (error) throw error;
    return data as AssetGroup[];
  },

  /**
   * Lấy danh sách loại tài sản
   */
  async getAssetTypes(): Promise<AssetType[]> {
    const { data, error } = await supabase
      .from('asset_types')
      .select('*')
      .order('type_name', { ascending: true });

    if (error) throw error;
    return data as AssetType[];
  },

  /**
   * Lấy danh sách hình ảnh của tài sản
   */
  async getAssetImages(assetId: string): Promise<AssetImage[]> {
    const { data, error } = await supabase
      .from('asset_images')
      .select('*')
      .eq('asset_id', assetId)
      .order('taken_at', { ascending: false });

    if (error) throw error;
    return data as AssetImage[];
  },

  /**
   * Upload hình ảnh tài sản
   */
  async uploadAssetImage(assetId: string, file: File, type: AssetImage['type'] = 'general'): Promise<AssetImage> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${assetId}/${Math.random()}.${fileExt}`;
    const filePath = `assets/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('maintenance')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('maintenance')
      .getPublicUrl(filePath);

    const { data, error } = await supabase
      .from('asset_images')
      .insert([{
        asset_id: assetId,
        image_url: publicUrl,
        type,
        taken_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (error) throw error;
    return data as AssetImage;
  },

  /**
   * Import tài sản từ Excel (Mock implementation for UI flow)
   */
  async importAssets(companyId: string, projectId: string, assets: any[]): Promise<void> {
    const { error } = await supabase
      .from('assets')
      .insert(assets.map(a => ({
        ...a,
        company_id: companyId,
        project_id: projectId,
        is_active: true
      })));

    if (error) throw error;
  }
};
